import type { TailorCvResponse } from "./profile-cv-tailor";
import type { KitAssembly } from "../utils/interview-kit/kit-facts";
import type { KitOutput } from "../utils/interview-kit/validate-kit-output";

export type UserStateKnownItemKey =
  | "profile"
  | "preferences"
  | "settings"
  | "emailAlerts"
  | "marketingConsent"
  | "pushToken"
  | "webPushSubscriptions"
  | "answerBank"
  | "matchFeedback"
  | "marketEmail";
export type UserStateSavedJobKey = `savedJob#${string}`;
export type UserStateViewedJobKey = `viewedJob#${string}`;
export type UserStateAppliedJobKey = `appliedJob#${string}`;
export type UserStateBrowsedJobKey = `browsedJob#${string}`;
export type UserStateFeedbackKey = `feedback#${string}`;
export type UserStateSavedSearchKey = `savedSearch#${string}`;
/** A user-organized folder/group of saved searches (see types/email-alerts.ts). */
export type UserStateSavedSearchFolderKey = `savedSearchFolder#${string}`;
/** companyId PKs are case-sensitive, never lowercase them. */
export type UserStateSavedCompanyKey = `savedCompany#${string}`;
/** A watched role from the normalised-title taxonomy (SEEKER_HUB_PLAN.md decision 4); the suffix is the localized normalisedTitle. */
export type UserStateSavedRoleKey = `savedRole#${string}`;
/** Persisted tailored-CV + cover-letter output for one job, see {@link ApplicationKitState}. */
export type UserStateApplicationKitKey = `applicationKit#${string}`;
/** Interview Kit v1 (TEC-725) generated prep kit for one job, see {@link InterviewKitState}. */
export type UserStateInterviewKitKey = `interviewKit#${string}`;
/** Application Autofill (TEC-489) pregenerated fill + outcome for one job, see {@link AutofillApplicationState}. */
export type UserStateAutofillApplicationKey = `autofillApplication#${string}`;
/**
 * Employer-portal membership grant (TEC-488): this user manages the company in the suffix.
 * companyId PKs are case-sensitive, never lowercase them. Deliberately NOT part of
 * {@link UserStateItemKey} / {@link isUserStateItemKey}: the generic /api/user-state routes
 * must keep rejecting it so a user can never self-grant company access, the only write path
 * is the operator-run harness/grant-employer-access.ts CLI (via the employer membership
 * repository).
 */
export type UserStateEmployerMembershipKey = `employerMembership#${string}`;
export type UserStateItemKey =
  | UserStateKnownItemKey
  | UserStateSavedJobKey
  | UserStateViewedJobKey
  | UserStateAppliedJobKey
  | UserStateBrowsedJobKey
  | UserStateFeedbackKey
  | UserStateSavedSearchKey
  | UserStateSavedSearchFolderKey
  | UserStateSavedCompanyKey
  | UserStateSavedRoleKey
  | UserStateApplicationKitKey
  | UserStateAutofillApplicationKey
  | UserStateInterviewKitKey;

export interface UserStateEntry<TData extends Record<string, unknown> = Record<string, unknown>> {
  data: TData;
  version: number;
  updatedAt: string | null;
}

export interface UserStateStoredItem<TData extends Record<string, unknown> = Record<string, unknown>>
  extends UserStateEntry<TData> {
  userId: string;
  itemKey: UserStateItemKey;
  updatedAt: string;
}

export type UserStateMap = Record<string, UserStateEntry>;

export const USER_STATE_INITIAL_VERSION = 1;
export const USER_STATE_CONFLICT_CODE = "USER_STATE_CONFLICT";

export function isSavedJobKey(itemKey: string): itemKey is UserStateSavedJobKey {
  return itemKey.startsWith("savedJob#") && itemKey.length > "savedJob#".length;
}

export function isViewedJobKey(itemKey: string): itemKey is UserStateViewedJobKey {
  return itemKey.startsWith("viewedJob#") && itemKey.length > "viewedJob#".length;
}

export function isAppliedJobKey(itemKey: string): itemKey is UserStateAppliedJobKey {
  return itemKey.startsWith("appliedJob#") && itemKey.length > "appliedJob#".length;
}

export function isBrowsedJobKey(itemKey: string): itemKey is UserStateBrowsedJobKey {
  return itemKey.startsWith("browsedJob#") && itemKey.length > "browsedJob#".length;
}

export function isFeedbackKey(itemKey: string): itemKey is UserStateFeedbackKey {
  return itemKey.startsWith("feedback#") && itemKey.length > "feedback#".length;
}

export function isSavedSearchKey(itemKey: string): itemKey is UserStateSavedSearchKey {
  return itemKey.startsWith("savedSearch#") && itemKey.length > "savedSearch#".length;
}

export function isSavedSearchFolderKey(itemKey: string): itemKey is UserStateSavedSearchFolderKey {
  return itemKey.startsWith("savedSearchFolder#") && itemKey.length > "savedSearchFolder#".length;
}

export function isSavedCompanyKey(itemKey: string): itemKey is UserStateSavedCompanyKey {
  return itemKey.startsWith("savedCompany#") && itemKey.length > "savedCompany#".length;
}

export function isSavedRoleKey(itemKey: string): itemKey is UserStateSavedRoleKey {
  return itemKey.startsWith("savedRole#") && itemKey.length > "savedRole#".length;
}

export function isApplicationKitKey(itemKey: string): itemKey is UserStateApplicationKitKey {
  return itemKey.startsWith("applicationKit#") && itemKey.length > "applicationKit#".length;
}

export function isAutofillApplicationKey(itemKey: string): itemKey is UserStateAutofillApplicationKey {
  return itemKey.startsWith("autofillApplication#") && itemKey.length > "autofillApplication#".length;
}

export function isInterviewKitKey(itemKey: string): itemKey is UserStateInterviewKitKey {
  return itemKey.startsWith("interviewKit#") && itemKey.length > "interviewKit#".length;
}

export function isEmployerMembershipKey(itemKey: string): itemKey is UserStateEmployerMembershipKey {
  return itemKey.startsWith("employerMembership#") && itemKey.length > "employerMembership#".length;
}

export function isUserStateItemKey(itemKey: string): itemKey is UserStateItemKey {
  if (
    itemKey === "profile" ||
    itemKey === "preferences" ||
    itemKey === "settings" ||
    itemKey === "emailAlerts" ||
    itemKey === "marketingConsent" ||
    itemKey === "pushToken" ||
    itemKey === "webPushSubscriptions" ||
    itemKey === "answerBank" ||
    itemKey === "matchFeedback" ||
    itemKey === "marketEmail"
  ) {
    return true;
  }
  return (
    isSavedJobKey(itemKey) ||
    isViewedJobKey(itemKey) ||
    isAppliedJobKey(itemKey) ||
    isBrowsedJobKey(itemKey) ||
    isFeedbackKey(itemKey) ||
    isSavedSearchKey(itemKey) ||
    isSavedSearchFolderKey(itemKey) ||
    isSavedCompanyKey(itemKey) ||
    isSavedRoleKey(itemKey) ||
    isApplicationKitKey(itemKey) ||
    isAutofillApplicationKey(itemKey) ||
    isInterviewKitKey(itemKey)
  );
}

export function createDefaultUserStateEntry(itemKey: UserStateItemKey): UserStateEntry {
  switch (itemKey) {
    case "profile":
      return {
        data: {},
        version: 0,
        updatedAt: null,
      };
    case "preferences":
      return {
        data: {},
        version: 0,
        updatedAt: null,
      };
    case "settings":
      return {
        data: {},
        version: 0,
        updatedAt: null,
      };
    default:
      return {
        data: {},
        version: 0,
        updatedAt: null,
      };
  }
}

/**
 * The `data` payload stored under an {@link UserStateApplicationKitKey} (`applicationKit#{jobId}`).
 * `profileHash` lets the client skip a re-generation on reopen when the profile snapshot used to
 * produce this kit hasn't changed (see stableHash in utils/text/stable-hash.ts).
 */
export type ApplicationKitState = {
  jobTitle: string;
  companyName: string;
  /** The job's DynamoDB sort key, needed to build a working job URL (see generateJobUrl); the display companyName alone 404s the primary getjob lookup. */
  companyId: string;
  locale: "en" | "fi" | "sv";
  profileHash: string;
  tailored?: TailorCvResponse;
  /** The user-edited final text, not the structured CoverLetterResponse, which the user may have hand-edited past the model's original shape. */
  coverLetter?: { text: string };
  /**
   * The last time the person downloaded a tailored CV for this job from the job page (TEC-711), so
   * My Jobs can show which CV went out. Kept across profile edits: it records what was sent.
   */
  cvSent?: { at: string; format: 'pdf' | 'docx'; template: string; changes: number };
  updatedAt: string;
};

/**
 * The `data` payload stored under an {@link UserStateInterviewKitKey} (`interviewKit#{jobId}`),
 * TEC-725. `factsHash`/`profileHash` let the route skip an LLM call when nothing changed.
 */
export type InterviewKitState = {
  jobId: string;
  companyId: string;
  locale: "en" | "fi" | "sv";
  jobTitle?: string;
  companyName?: string;
  assembly: KitAssembly;
  content: KitOutput;
  /** True when the kit was built from the CV tailored for this job rather than the general profile (TEC-711). */
  usedTailoredCv?: boolean;
  factsHash: string;
  profileHash: string;
  generatedAt: string;
  droppedCount: number;
};

/**
 * The `data` payload stored under an {@link UserStateAutofillApplicationKey}
 * (`autofillApplication#{jobId}`), TEC-489 Application Autofill, Phase 1 (Teamtailor only).
 * Single source of truth the pregenerate route, the review/confirm UI, and the fill+submit
 * Lambda all read/write. Field shape (`firstName`/`lastName`/`email`/`phone`/`coverLetter`,
 * `answers[].type` of `text | choice | boolean`) mirrors what was found live on real Teamtailor
 * application forms during discovery: consistent `candidate[first_name]`-style structured fields
 * across every company, plus per-job custom `answers_attributes` screening questions that are
 * either free text, a single-select choice (e.g. work-authorization status), or a yes/no boolean.
 */
/**
 * `preparing` is written while pregenerate is still running, carrying `progress` only (no fields
 * or answers yet), so the review UI can show each real step as it happens.
 */
export type AutofillApplicationStatus = "preparing" | "pregenerated" | "confirmed" | "submitted" | "failed" | "manual_fallback";

/** Where pregenerate has got to, with the real counts so far (shown as a live checklist). */
export interface AutofillPrepareProgress {
  step: "opening_form" | "matching" | "drafting" | "done";
  questionCount?: number;
  answeredFromProfile?: number;
  drafting?: number;
}

export type AutofillApplicationFailureReason =
  | "bot_detection"
  | "timeout"
  | "unrecognized_form"
  | "rejected"
  | "unknown";

export interface AutofillAnswerDraft {
  /** Stable id from the live form at pregenerate time (e.g. the ATS's own question id), re-fetched at fill time, never assumed unchanged. */
  questionId: string;
  questionText: string;
  type: "text" | "choice" | "boolean";
  /** For `type: "choice"`: the option labels as seen on the live form at pregenerate time. */
  choices?: string[];
  /** Pregenerated value (LLM-drafted free text, or the choice/boolean picked from the user's profile), user-editable before confirm. */
  value: string;
  /**
   * Where the value came from (TEC-719): the answer bank, a profile field, an LLM draft, or the
   * user's own edit in the review modal. Absent on items written before TEC-719.
   */
  source?: "bank" | "profile" | "llm" | "user";
  /** The answer-bank or profile key the value was matched from, or 'saved' for a saved answer. */
  bankKey?: string;
  /** For LLM drafts: a short note on what the draft was based on ("Your role as Data Analyst at Kesko"). */
  basis?: string;
}

export interface AutofillApplicationState {
  jobId: string;
  companyId: string;
  /** Snapshot taken at pregenerate (TEC-714) so the fill Lambda can write a complete appliedJob# entry without another lookup. */
  jobTitle?: string;
  companyName?: string;
  /** Grows as more ATS recipes are added, see lambda/crawler/utils/apply-autofill/ats-recipe-registry.ts. */
  atsVendor: "teamtailor" | "greenhouse" | "ashby" | "lever" | "personio" | "recruitee" | "workable";
  status: AutofillApplicationStatus;
  failureReason?: AutofillApplicationFailureReason;
  /** Human-readable detail for the review UI's failure banner, never a generic "something went wrong" (see TEC-489's failure-handling requirement). */
  failureMessage?: string;
  applyUrl: string;
  fields: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    coverLetter?: string;
  };
  answers: AutofillAnswerDraft[];
  /** S3 key of the résumé to attach, same `cv-uploads/{userId}/{uuid}.pdf` convention as profile-cv.ts. */
  cvFileKey: string;
  confirmedAt?: string;
  submittedAt?: string;
  updatedAt: string;
  /** Only while `status === 'preparing'`. */
  progress?: AutofillPrepareProgress;
}

